import axios from '../utils/axios';

/**
 * Session-level scan of uploaded Nimbus episode .db files (Options →
 * Nimbus Recordings → "Scan recordings"). The server opens each
 * episode's uploaded whole-.db, compares it against the blob chunks it
 * received live, cross-checks the .db runs table against RunData, and
 * flags windows where at least two of wheel speed / dynamic pressure /
 * power / GPS move together with no run covering them.
 */

export interface ScanTableInfo {
  db_rows: number;
  db_ts_min_ms: number | null;
  db_ts_max_ms: number | null;
  server_rows: number;
  server_chunks: number;
  server_ts_min_ms: number | null;
  server_ts_max_ms: number | null;
  db_has_more: boolean;
}

export interface ScanRunInfo {
  run_uuid: string;
  run_number: number | null;
  description: string;
  start_ts_ms: number | null;
  end_ts_ms: number | null;
  partial: boolean;
  // false = the run exists only as server RunData (e.g. a scan-recovered
  // window) with no .db runs entry — re-import does not apply to it.
  in_db: boolean;
  on_server: boolean;
  rundata_id: string | null;
  run_status: number | null;
  // The last stream build's failure for this run, if any
  // (rundata.stream_build.error).
  build_error: string | null;
}

export interface MissingRunCandidate {
  start_ts_ms: number;
  end_ts_ms: number;
  duration_s: number;
  channels: string[];
}

export interface EpisodeScan {
  episode_id: string;
  device_id: string | null;
  start_ts_ms: number;
  end_ts_ms: number | null;
  db_upload_state: string | null;
  db_backup_verified_at: string | null;
  file: { filename: string; s3_key: string; captured_ts: string } | null;
  reason?: string;
  error?: string;
  tables?: Record<string, ScanTableInfo>;
  runs?: ScanRunInfo[];
  missing_run_candidates?: MissingRunCandidate[];
  update_available?: boolean;
}

export interface SessionEpisodeScan {
  session_id: string;
  episodes: EpisodeScan[];
}

/** Scan every episode of the session. Slow (may download .db files from S3). */
export async function scanSessionEpisodes(sessionId: string): Promise<SessionEpisodeScan> {
  return axios.post(`/episode-db-scan/session/${sessionId}`).then((res) => res.data);
}

/**
 * "Update blob info": dispatch the idempotent .db ingest for one uploaded
 * file — rebuilds run zips whose coverage grew and creates RunData rows
 * for runs the server never saw. Runs in the background server-side.
 */
export async function ingestEpisodeDb(filename: string): Promise<{ status: string; filename: string }> {
  return axios.post('/episode-dbs-s3/ingest', { filename }).then((res) => res.data);
}

export interface RecoveryResult {
  outcome: 'created' | 'attached' | 'exists' | 'rebuilt' | 'adopted' | 'skipped';
  rundata_id?: string;
  config_id?: string | null;
  // {table_name: rows inserted into the chunk store from the .db}
  backfilled: Record<string, number>;
}

/**
 * "Add run": recover a potential-missing-run window as a RunData built
 * from the .db — attached to the chosen session config, or unattached
 * when no configId is given. Idempotent on the artifact — the run UUID
 * derives from the window, so repeating finds the existing row — but NOT
 * on the attachment: repeating with a configId is how a run recovered
 * unattached gets placed, and that returns 'attached' rather than
 * 'exists'.
 */
export async function addMissingRun(
  episodeId: string,
  startTsMs: number,
  endTsMs: number,
  configId?: string,
): Promise<RecoveryResult> {
  return axios
    .post(`/episode-db-scan/episode/${episodeId}/add-run`, {
      start_ts_ms: startTsMs,
      end_ts_ms: endTsMs,
      ...(configId ? { config_id: configId } : {}),
    })
    .then((res) => res.data);
}

/**
 * "Re-import": backfill the run window's missing rows into the chunk
 * store, force-rebuild the run's artifact from the .db, and reprocess it
 * if it was already processed.
 */
export async function reimportRun(episodeId: string, runUuid: string): Promise<RecoveryResult> {
  return axios
    .post(`/episode-db-scan/episode/${episodeId}/reimport-run`, { run_uuid: runUuid })
    .then((res) => res.data);
}
